export const summerThemeCss = `
	/* Meeting page: add padding and border to header and editor so they
	   don't run edge-to-edge against the beach background */
	.summer-theme [data-component="meeting-header"] {
		padding: var(--chakra-space-3);
		border: 1px solid var(--chakra-colors-border-default);
	}
	.summer-theme [data-component="one-on-one-view"] :has(> .shared-editor-container) {
		border-radius: var(--chakra-radii-lg);
		overflow: hidden;
	}
	.summer-theme [data-component="one-on-one-view"] .shared-editor-container {
		padding: var(--chakra-space-3);
		border: 1px solid var(--chakra-colors-border-default);
		border-radius: var(--chakra-radii-lg);
	}
	.summer-theme [data-component="one-on-one-view"] [data-component="completed-meeting-view"] {
		padding: var(--chakra-space-3);
		border: 1px solid var(--chakra-colors-border-default);
	}

	/* Bizy side panel: frosted surface so messages stay readable over the
	   beach background. */
	.summer-theme [data-component="bizy-side-panel"] {
		background: var(--chakra-colors-whiteAlpha-900) !important;
		backdrop-filter: blur(44px);
		-webkit-backdrop-filter: blur(44px);
	}
	html[data-theme="dark"].summer-theme [data-component="bizy-side-panel"] {
		background: var(--chakra-colors-blackAlpha-900) !important;
	}

	/* Meeting context drawer (fob + expanded panel) and the header dropdown
	   panel (History / Info): frosted surface so content stays readable over
	   the beach. */
	.summer-theme [data-component="meeting-context-drawer-fob"],
	.summer-theme [data-component="meeting-context-drawer-panel"],
	.summer-theme [data-component="meeting-header-dropdown-panel"] {
		background: var(--chakra-colors-whiteAlpha-900) !important;
		backdrop-filter: blur(44px);
		-webkit-backdrop-filter: blur(44px);
	}
	html[data-theme="dark"].summer-theme [data-component="meeting-context-drawer-fob"],
	html[data-theme="dark"].summer-theme [data-component="meeting-context-drawer-panel"],
	html[data-theme="dark"].summer-theme [data-component="meeting-header-dropdown-panel"] {
		background: var(--chakra-colors-blackAlpha-900) !important;
	}

	/* Scroll-fade gradients inside the frosted panels use bg-canvas as the
	   opaque end of the gradient. In summer dark mode bg-canvas is transparent,
	   so the fade disappears. Override to match the frosted panel surface. */
	.summer-theme [data-component="context-drawer-scroll-fade"] {
		background: linear-gradient(to bottom, var(--chakra-colors-whiteAlpha-900) 0%, transparent 100%) !important;
	}
	html[data-theme="dark"].summer-theme [data-component="context-drawer-scroll-fade"] {
		background: linear-gradient(to bottom, var(--chakra-colors-blackAlpha-900) 0%, transparent 100%) !important;
	}

	/* ================= data-on-scenic-bg =================
	   Shared treatment for anything floating directly over the scenic
	   sky/ocean background in light mode (dark mode already resolves text
	   tokens to light values, so it needs nothing).

	   Mark a text element or container with data-on-scenic-bg:
	   - the element itself and any h1/h2/h3/p/a inside turn white with a soft
	     teal shadow (survives white clouds drifting behind);
	   - svg icons inside go white with a matching drop-shadow;
	   - DIRECT-child buttons (tab pills, action pills) — or a marked button
	     itself — get white text/border and a translucent white hover pill
	     (their normal bg.subtle/bgAlpha hovers would swallow white text).
	     Direct-child only, so dropdown panels rendered as sibling divs keep
	     dark text on their frosted surfaces;
	   - a pill flagged data-active (see PillTab) inverts to a solid white
	     pill with dark text — the strongest state signal over a bright sky;
	   - content inside a .chakra-menu__menu-list is always excluded: a menu
	     panel brings its own opaque surface, so its items keep their normal
	     colors even when the menu's trigger lives in a marked container
	     (e.g. the Advice / Spotlight dropdowns in the give-box action row). */
	html[data-theme="light"].summer-theme [data-on-scenic-bg]:not(.chakra-menu__menu-list *),
	html[data-theme="light"].summer-theme [data-on-scenic-bg] :is(h1, h2, h3, p, a):not(.chakra-menu__menu-list *) {
		color: #FFFFFF !important;
		text-shadow: 0 1px 12px rgba(11, 95, 89, 0.45);
	}
	html[data-theme="light"].summer-theme [data-on-scenic-bg] svg:not(.chakra-menu__menu-list *) {
		color: #FFFFFF !important;
		filter: drop-shadow(0 1px 6px rgba(11, 95, 89, 0.45));
	}
	/* The :not() guards above keep scenic rules from targeting menu content,
	   but the marked container's white still reaches unstyled menu text via
	   inheritance — reset the panel so its items inherit dark text again. */
	html[data-theme="light"].summer-theme [data-on-scenic-bg] .chakra-menu__menu-list {
		color: var(--chakra-colors-text-default) !important;
		text-shadow: none;
	}
	/* ...but NOT icons inside nested buttons (button below an intermediate
	   element, e.g. the calendar icon in the month-picker input): those
	   buttons carry their own light surface, so their icons keep their own
	   color. Direct-child pill buttons are unaffected — they have no
	   intermediate element. */
	html[data-theme="light"].summer-theme [data-on-scenic-bg] * > button svg {
		color: inherit !important;
		filter: none;
	}
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button,
	html[data-theme="light"].summer-theme button[data-on-scenic-bg] {
		color: #FFFFFF !important;
		border-color: #FFFFFF !important;
		text-shadow: 0 1px 12px rgba(11, 95, 89, 0.45);
	}
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button:hover,
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button:active,
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button[aria-expanded="true"],
	html[data-theme="light"].summer-theme button[data-on-scenic-bg]:hover,
	html[data-theme="light"].summer-theme button[data-on-scenic-bg]:active {
		background: rgba(255, 255, 255, 0.22) !important;
	}
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button[data-active],
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button[data-active]:hover {
		background: #FFFFFF !important;
		color: var(--chakra-colors-gray-900) !important;
		border-color: transparent !important;
		text-shadow: none;
	}
	/* Labels rendered as p/svg inside an active pill must follow the pill's
	   dark text, not the generic white-descendant rule above. */
	html[data-theme="light"].summer-theme [data-on-scenic-bg] > button[data-active] :is(p, svg) {
		color: var(--chakra-colors-gray-900) !important;
		text-shadow: none;
		filter: none;
	}
	/* Variant: data-on-scenic-bg="scrim" — for elements over the variable
	   parts of the scene (sand, water) where neither plain dark nor plain
	   white text survives everywhere: a frosted white pill with dark text,
	   matching the give-box / search frosted surfaces. White at 0.5 + 44px
	   blur keeps gray.900 text >= 4.5:1 (WCAG AA) over anything in this
	   scene — the blur averages the backdrop, and the darkest blurred patch
	   (mid-water) still composites well above the AA threshold. Don't drop
	   below ~0.4 without rechecking against the darkest scene content.
	   Declared after the base rules so it wins over the white-text +
	   glass-hover treatment. */
	html[data-theme="light"].summer-theme [data-on-scenic-bg="scrim"],
	html[data-theme="light"].summer-theme button[data-on-scenic-bg="scrim"] {
		background: rgba(255, 255, 255, 0.5) !important;
		backdrop-filter: blur(44px);
		-webkit-backdrop-filter: blur(44px);
		color: var(--chakra-colors-gray-900) !important;
		border-color: transparent !important;
		text-shadow: none;
	}
	html[data-theme="light"].summer-theme [data-on-scenic-bg="scrim"] svg {
		color: var(--chakra-colors-gray-900) !important;
		filter: none;
	}
	html[data-theme="light"].summer-theme [data-on-scenic-bg="scrim"]:hover,
	html[data-theme="light"].summer-theme button[data-on-scenic-bg="scrim"]:hover,
	html[data-theme="light"].summer-theme [data-on-scenic-bg="scrim"]:active,
	html[data-theme="light"].summer-theme button[data-on-scenic-bg="scrim"]:active {
		background: rgba(255, 255, 255, 0.65) !important;
	}

	/* Meetings search input floats directly on the sky; give it the same
	   frosted minipage surface as the give-box recognition pill (bg.minipage
	   + blur). The token flips to blackAlpha in dark mode, so no per-mode
	   rules needed. Placeholder/icon already use text.softer like the pill. */
	.summer-theme [data-component="meetings-search"] input {
		background: var(--chakra-colors-bg-minipage) !important;
		backdrop-filter: blur(44px);
		-webkit-backdrop-filter: blur(44px);
		border-color: transparent !important;
	}

	/* Styling for Menu lists (main and submenus) in Summer Theme.
	   Light mode keeps the default solid bg.menu surface (a translucent
	   white looked muddy over the scene); dark mode gets a frosted black. */
	.summer-theme .chakra-menu__menu-list {
		border-color: var(--chakra-colors-border-default) !important;
	}
	html[data-theme="dark"].summer-theme .chakra-menu__menu-list {
		background: var(--chakra-colors-blackAlpha-900) !important;
		backdrop-filter: blur(44px) !important;
		-webkit-backdrop-filter: blur(44px) !important;
		border-color: var(--chakra-colors-border-default) !important;
	}

	/* Hover/Focus/Active styles for Menu items in Summer Theme */
	.summer-theme .chakra-menu__menu-item {
		border-radius: var(--chakra-radii-md) !important;
		transition: background 0.15s ease, color 0.15s ease !important;
	}
	.summer-theme .chakra-menu__menu-item:hover,
	.summer-theme .chakra-menu__menu-item:focus,
	.summer-theme .chakra-menu__menu-item[data-active] {
		background: var(--chakra-colors-primary-bgAlpha) !important;
		color: var(--chakra-colors-primary-fgStrongest) !important;
	}

	/* Styling for expanded nav menu buttons in Summer Theme */
	.summer-theme [data-testid="user-nav-menu-button"][aria-expanded="true"] {
		background: var(--chakra-colors-primary-bgAlpha) !important;
		color: var(--chakra-colors-primary-fgStrongest) !important;
	}
	.summer-theme [data-testid="user-nav-menu-button"][aria-expanded="true"] * {
		color: var(--chakra-colors-primary-fgStrongest) !important;
	}
`;
